import useUpdateProfile from "api/UserResource/useUpdateProfile";
import { Form, Formik } from "formik";
import { phoneRegex } from "utils";
import * as Yup from "yup";
import { FiCheck, FiSave } from "react-icons/fi";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import useSearchParams from "hooks/useSearchParams";
import AccountField from "./AccountField";

const UpdateProfile = ({ userId, firstName, lastName, mobileNumber, dob, sex }) => {
	const { getParam } = useSearchParams();
	const { mutateAsync, isLoading } = useUpdateProfile(userId, getParam("returnUrl") || "");
	const initialValues = {
		first_name: firstName || "",
		last_name: lastName || "",
		mobile_number: mobileNumber || "",
		dob: dob || "",
		sex: sex || "",
	};
	const schema = Yup.object().shape({
		first_name: Yup.string().required("First name is required"),
		last_name: Yup.string().required("Surname is required"),
		mobile_number: Yup.string()
			.matches(phoneRegex, "Use a valid phone number with country code")
			.min(10, "Use at least 10 characters")
			.max(20, "Use no more than 20 characters"),
		dob: Yup.string().matches(
			/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/,
			"Enter a valid date",
		),
	});
	return (
		<Formik
			initialValues={initialValues}
			enableReinitialize
			validationSchema={schema}
			onSubmit={async (values, { resetForm, setStatus }) => {
				try {
					await mutateAsync(values);
					resetForm({ values });
					setStatus("saved");
				} catch (_) {
					setStatus("error");
				}
			}}
		>
			{({ dirty, isSubmitting, status, resetForm }) => (
				<Form>
					<div className="account-section-heading">
						<h2>About you</h2>
						<p>Your first and last name are required. Everything else is optional.</p>
					</div>
					<div className="account-form-grid">
						<AccountField name="first_name" label="First name" autoComplete="given-name" required />
						<AccountField name="last_name" label="Last name" autoComplete="family-name" required />
						<AccountField
							name="mobile_number"
							type="phone"
							label="Phone number"
							autoComplete="tel"
							optional
							placeholder="+263 77 123 4567"
						/>
						<AccountField
							name="dob"
							type="date"
							label="Date of birth"
							autoComplete="bday"
							optional
						/>
						<AccountField
							name="sex"
							type="select"
							label="Gender"
							optional
							options={[
								{ label: "Male", value: "Male" },
								{ label: "Female", value: "Female" },
							]}
						/>
					</div>
					<div className="account-form-actions">
						<p role="status" aria-live="polite">
							{isLoading
								? "Saving your details…"
								: dirty
									? "You have unsaved changes"
									: status === "saved"
										? "Your details are up to date"
										: status === "error"
											? "Couldn’t save. Please try again."
											: "Make this space yours."}
						</p>
						<div className="account-form-buttons">
							{dirty && (
								<button
									type="button"
									className="account-button secondary"
									disabled={isSubmitting}
									onClick={() => resetForm()}
								>
									Discard
								</button>
							)}
							<button
								type="submit"
								className="account-button"
								disabled={isLoading || isSubmitting || !dirty}
							>
								{isLoading ? (
									<AiOutlineLoading3Quarters className="animate-spin" aria-hidden="true" />
								) : status === "saved" && !dirty ? (
									<FiCheck aria-hidden="true" />
								) : (
									<FiSave aria-hidden="true" />
								)}
								{isLoading ? "Saving…" : status === "saved" && !dirty ? "Saved" : "Save changes"}
							</button>
						</div>
					</div>
				</Form>
			)}
		</Formik>
	);
};
export default UpdateProfile;
